package com.gsb.vyapar.bhagya.presentation.splash.util

import androidx.compose.ui.platform.UriHandler
import com.gsb.vyapar.bhagya.datasource.cache.SqlDelightClient
import com.gsb.vyapar.bhagya.datasource.network.KtorClient
import com.gsb.vyapar.bhagya.datasource.preference.Preference
import com.gsb.vyapar.bhagya.datasource.runtimecache.RuntimeCache
import com.gsb.vyapar.bhagya.domain.Config
import com.gsb.vyapar.bhagya.domain.*
import com.gsb.vyapar.bhagya.domain.Info
import com.gsb.vyapar.bhagya.domain.SubCategories
import com.gsb.vyapar.bhagya.domain.decodeCategoriesString
import com.gsb.vyapar.bhagya.domain.decodeSubCategoriesString
import com.gsb.vyapar.bhagya.domain.encodeCategoriesString
import com.gsb.vyapar.bhagya.domain.encodeConfigString
import com.gsb.vyapar.bhagya.domain.encodeInfoString
import com.gsb.vyapar.bhagya.domain.encodeSubCategoriesString
import com.gsb.vyapar.bhagya.domain.toCategories
import com.gsb.vyapar.bhagya.domain.toConfig
import com.gsb.vyapar.bhagya.domain.toInfo
import com.gsb.vyapar.bhagya.domain.toSubCategories
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.getState
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.launchDefault
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.updateState
import com.gsb.vyapar.bhagya.interactor.util.showError
import com.gsb.vyapar.bhagya.presentation.Screens.*
import com.gsb.vyapar.bhagya.presentation.business.uistate.getBusinessUiState
import com.gsb.vyapar.bhagya.presentation.business.util.getBusiness
import com.gsb.vyapar.bhagya.presentation.business.util.getBusinessIdUrl
import com.gsb.vyapar.bhagya.presentation.business.util.getBusinessSuspend
import com.gsb.vyapar.bhagya.presentation.dialog.Dialogs
import com.gsb.vyapar.bhagya.presentation.dialog.uistate.DialogUiState
import com.gsb.vyapar.bhagya.util.BuildConfig
import com.gsb.vyapar.bhagya.util.PlatformType
import com.gsb.vyapar.bhagya.util.getAndroid
import com.gsb.vyapar.bhagya.util.getIos
import com.gsb.vyapar.bhagya.util.getMessage
import com.gsb.vyapar.bhagya.util.getWebApp
import com.gsb.vyapar.bhagya.util.getWebsite
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope

fun goToNavigation() {
    updateState(getState().copy(mainView = Navigation))
}

fun getLastBusinessNumber(): Int {
    return getState().getConfig().getBusinessNumber()
}

suspend fun checkToken() {
    if (RuntimeCache.accessToken.isEmpty()) {
        val result = KtorClient.anonymousLogin()
        if (result.isSuccess) {
            val authResponse = result.getOrNull()
            RuntimeCache.setTokens(authResponse?.idToken.orEmpty(), authResponse?.refreshToken.orEmpty())
        }
    }
}

suspend fun getConfig() {
    try {
        val config = getState().getConfig()
        val version = KtorClient.getConfigVersion()
        if (config.v < (version.getOrNull() ?: 0)) {
            getConfigAPI()
        }
    } catch (e: Exception) {
        getConfigAPI()
    }
}

private suspend fun getConfigAPI() {
    val result = KtorClient.getConfig()
    if (result.isSuccess) {
        updateState(getState().copy(config = result.getOrNull()?.toConfig()?.encodeConfigString() ?: Config().encodeConfigString()))
        Preference.config = getState().config
    } else {
        showError("Fetch Config failure : ${result.exceptionOrNull().getMessage()}")
    }
}

suspend fun getCategories() {
    try {
        val config = getState().getConfig()
        val categories = getState().categories.decodeCategoriesString()
        if (categories.v < (config.d.config[CATEGORY] ?: 0)) {
            getCategoriesAPI()
        }
    } catch (e: Exception) {
        getCategoriesAPI()
    }
}

private suspend fun getCategoriesAPI() {
    val result = KtorClient.getCategories()
    if (result.isSuccess) {
        updateState(getState().copy(categories = result.getOrNull()?.toCategories()?.encodeCategoriesString() ?: com.gsb.vyapar.bhagya.domain.Categories().encodeCategoriesString()))
        Preference.categories = getState().categories
        SqlDelightClient.updateCategories(getState().categories.decodeCategoriesString())
    } else {
        showError("Fetch Categories failure : ${result.exceptionOrNull().getMessage()}")
    }
}

suspend fun getSubCategories() {
    try {
        val config = getState().getConfig()
        val subCategories = getState().subCategories.decodeSubCategoriesString()
        if (subCategories.v < (config.d.config[SUBCATEGORY] ?: 0)) {
            getSubCategoriesAPI()
        }
    } catch (e: Exception) {
        getSubCategoriesAPI()
    }
}

private suspend fun getSubCategoriesAPI() {
    val result = KtorClient.getSubCategories()
    if (result.isSuccess) {
        updateState(getState().copy(subCategories = result.getOrNull()?.toSubCategories()?.encodeSubCategoriesString() ?: SubCategories().encodeSubCategoriesString()))
        Preference.subCategories = getState().subCategories
        SqlDelightClient.updateSubCategories(getState().subCategories.decodeSubCategoriesString())
    } else {
        showError("Fetch SubCategories failure : ${result.exceptionOrNull().getMessage()}")
    }
}

suspend fun getInfo() {
    try {
        val config = getState().getConfig()
        val info = getState().getInfo()
        if (info.v < (config.d.config[INFO] ?: 0)) {
            getInfoAPI()
        }
    } catch (e: Exception) {
        getInfoAPI()
    }
}

private suspend fun getInfoAPI() {
    val result = KtorClient.getInfo()
    if (result.isSuccess) {
        updateState(getState().copy(info = result.getOrNull()?.toInfo()?.encodeInfoString() ?: Info().encodeInfoString()))
        Preference.info = getState().info
    } else {
        showError("Fetch Info failure : ${result.exceptionOrNull().getMessage()}")
    }
}

suspend fun getBusinesses(businessNumber: Int) {
    try {
        val config = getState().getConfig()
        if (config.getBusinessNumber() > businessNumber) {
            getBusinessesAPI(businessNumber)
        }
    } catch (e: Exception) {
        showError("Fetch Businesses failure : ${e.getMessage()}")
    }
}

private suspend fun getBusinessesAPI(businessNumber: Int) {
    val result = KtorClient.getBusinessIndexAll(businessNumber)
    if (result.isSuccess) {
        val businesses = result.getOrNull()
        if (businesses != null) {
            businesses.forEach {
                val business = it.value.toBusiness()
                SqlDelightClient.updateBusiness(business)
            }
        } else {
            showError("Fetch Businesses failure : null")
        }
    } else {
        showError("Fetch Businesses failure : ${result.exceptionOrNull().getMessage()}")
    }
}

suspend fun getAllTheBusiness() {
    getBusinessUiState().updateBusinessList(SqlDelightClient.getAllTheBusinesses())
}

suspend fun copyBusinessesToSql() {
    val businessNumber = getState().getConfig().getBusinessNumber()
    if (businessNumber == 1) return
    coroutineScope {
        (1 until businessNumber).map { index ->
            async(Dispatchers.Default) {
                getBusinessSuspend(index)?.let { SqlDelightClient.updateBusiness(it) }
            }
        }.awaitAll()
    }
}

fun checkVersion(uriHandler: UriHandler) {
    val info = getState().getInfo()
    if (isVersionGreater(BuildConfig.getVersion(), info.getAppVersion())) {
        showUpdateRequiredPopup(uriHandler)
    } else {
        goToNavigation()
    }
}

fun showUpdateRequiredPopup(uriHandler: UriHandler) {
    updateState(getState().copy(dialogUiState = DialogUiState(dialogType = Dialogs.Message, message = "A new version of GSB-VyaparBhagya is available with important improvements and fixes. To continue using the app, please update to the latest version.", buttonString = "Update", onOKClick = {
        when (BuildConfig.platform()) {
            PlatformType.ANDROID -> uriHandler.openUri(getAndroid())
            PlatformType.DESKTOP -> uriHandler.openUri(getWebsite())
            PlatformType.IOS -> uriHandler.openUri(getIos())
            PlatformType.JS -> uriHandler.openUri(getWebApp())
            PlatformType.WASM -> uriHandler.openUri(getWebApp())
        }
    })))
}

fun isVersionGreater(current: String, newVersion: String): Boolean {
    try {
        val currentParts = current.split(".").map { it.toInt() }
        val newParts = newVersion.split(".").map { it.toInt() }
        for (i in 0 until minOf(currentParts.size, newParts.size)) {
            if (newParts[i] > currentParts[i]) return true
            if (newParts[i] < currentParts[i]) return false
        }
        return newParts.size > currentParts.size
    } catch (e: Exception) {
        return false
    }
}